import { useContext, useRef, useState, useEffect } from "react";
import { RequestLoader, ToastContext } from "../../../hooks/context";
import {
  cleanObject,
  getRandomIntInclusive,
  getUrlQuerysection,
} from "../../../helpers";
import {
  useAddCityToLocation,
  useCreateShippingMutation,
  useEditShippingMutation,
} from "../../../hooks/api/mutations/useInventory";
import { useGetMenus } from "../../../hooks/api/queries";
import {
  useGetCountries,
  useGetCountryDetails,
} from "../../../hooks/api/queries/useBusiness";
import { useQueryClient } from "react-query";

const useShippingForm = ({
  shippingInfo,
  refetch,
  closeWidget,
  isEditMode,
}) => {
  const queries = getUrlQuerysection();
  const { data: countries, isLoading: isLoadingCountries } = useGetCountries();
  const queryClient = useQueryClient();
  const reLabelDefault = (menus, nameKey = "name", idKey = "id") => {
    let newArray = [];
    menus?.forEach((menu) => {
      newArray?.push({
        value: menu?.[idKey],
        label: menu?.[nameKey],
      });
    });
    return newArray;
  };
  const [shippingDetails, setShippingDetails] = useState({
    name: shippingInfo?.name || "",
    countryId: shippingInfo?.country
      ? shippingInfo?.country?.emoji + " " + shippingInfo?.country?.name
      : "",
    delivery_window: shippingInfo?.delivery_window || "",
    storefront_ids: shippingInfo?.storefronts
      ? shippingInfo?.storefronts?.map((menu) => menu?.name)
      : "",
    stateIds: shippingInfo?.states ? reLabelDefault(shippingInfo?.states) : [],
    rate: shippingInfo?.rate || "",
    includes_holiday: shippingInfo?.includes_holiday || false,
    cityIds: shippingInfo?.cities ? reLabelDefault(shippingInfo?.cities) : [],
    pricing_rules:
      shippingInfo?.pricing_rules?.map((item) => {
        return cleanObject({
          city_id: item?.city?.id,
          state_id: item?.state?.id,
          country_id: item?.country?.id,
          ...item,
        });
      }) || [],
  });

  const { data: menus, isLoading: isLoadingMenus } = useGetMenus({ queries });
  const [addCity, setAddCity] = useState(null);
  const [addCityModal, setAddCityModal] = useState(null);
  const [currency, setCurrency] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState([]);
  const [cities, setCities] = useState({});
  const { setRequestLoaderProgress } = useContext(RequestLoader);
  const triggerToast = useContext(ToastContext);
  const createShippingMutation = useCreateShippingMutation();
  const editShippingMutation = useEditShippingMutation();
  const {
    mutate: addCityToLocationMutation,
    data: cityLocationData,
    isLoading: isLoadingcityLocationData,
  } = useAddCityToLocation();
  const { data: countryDetails, isLoading: isLoadingCountryDetails } =
    useGetCountryDetails(selectedCountry);
  const selectInputRef = useRef();

  useEffect(() => {
    if (isEditMode && shippingInfo?.country?.id) {
      setSelectedCountry(shippingInfo?.country?.id);
    }

    if (isEditMode && shippingInfo?.storefronts) {
      const getMenuIds = shippingInfo?.storefronts?.map((menu) => menu?.id);
      setShippingDetails((prevDetails) => ({
        ...prevDetails,
        storefront_ids: getMenuIds,
      }));
    }
  }, []);

  useEffect(() => {
    if (isEditMode && shippingInfo?.states) {
      const getStates = shippingInfo?.states?.map((data) => ({
        label: data?.name,
        value: data?.id,
      }));
      setSelectedState(getStates);
      const initialCities = {};

      shippingInfo?.states?.forEach((state) => {
        const cityIds = shippingInfo?.cities
          ?.filter((city) => city?.state?.id === state?.id)
          ?.map((city) => city?.id);

        if (cityIds) {
          initialCities[state?.id] = cityIds;
        }
      });
      setCities(initialCities);
    }
  }, []);

  // --- Pricing Rules helpers ---

  const matchRule = (r, country_id, state_id, city_id) => {
    if (city_id) return `${r.city_id}` === `${city_id}`;
    if (state_id) return `${r.state_id}` === `${state_id}`;
    return `${r.country_id}` === `${country_id}`;
  };

  const buildRuleKey = (country_id, state_id, city_id) => {
    if (city_id) return { city_id };
    if (state_id) return { state_id };
    return { country_id };
  };

  const getPricingRule = (country_id, state_id = "", city_id = "") => {
    return shippingDetails.pricing_rules?.find((r) =>
      matchRule(r, country_id, state_id, city_id),
    );
  };

  const upsertPricingRule = (country_id, state_id = "", city_id = "") => {
    setShippingDetails((prev) => {
      const rules = [...(prev.pricing_rules || [])];
      const exists = rules.some((r) =>
        matchRule(r, country_id, state_id, city_id),
      );
      if (!exists) {
        rules.push({
          ...buildRuleKey(country_id, state_id, city_id),
          rate: "",
          delivery_window: "",
          includes_holiday: false,
        });
      }
      return { ...prev, pricing_rules: rules };
    });
  };

  const updatePricingRule = (
    country_id,
    state_id = "",
    city_id = "",
    field,
    value,
  ) => {
    setShippingDetails((prev) => ({
      ...prev,
      pricing_rules: prev.pricing_rules?.map((r) =>
        matchRule(r, country_id, state_id, city_id)
          ? { ...r, [field]: value }
          : r,
      ),
    }));
  };

  const removePricingRule = (country_id, state_id = "", city_id = "") => {
    setShippingDetails((prev) => ({
      ...prev,
      pricing_rules: prev.pricing_rules?.filter(
        (r) => !matchRule(r, country_id, state_id, city_id),
      ),
    }));
  };

  // --- End Pricing Rules helpers ---

  const handleAddShipping = (check) => {
    setRequestLoaderProgress(getRandomIntInclusive(10, 50));
    const {
      cityIds,
      stateIds,
      countryId,
      pricing_rules,
      ...otherShippingDetails
    } = shippingDetails;
    const country_id = selectedCountry;
    const city_ids = Object.values(cities).flat();
    const state_ids = selectedState?.map((data) => data?.value);
    const data = {
      ...otherShippingDetails,
      country_id,
      city_ids,
      state_ids,
      pricing_rules: (pricing_rules || [])
        .filter((r) => r.rate !== "")
        .map((r) => cleanObject({ ...r })),
    };
    createShippingMutation.mutate(data, {
      onSuccess: () => {
        refetch();
        triggerToast("shipping added successfully", "success");
        if (check !== "continue") {
          closeWidget();
        }
        setSelectedState([]);
        setCities({});
      },
    });
  };

  const handleEditShipping = () => {
    setRequestLoaderProgress(getRandomIntInclusive(10, 50));

    const {
      cityIds,
      stateIds,
      countryId,
      storefront_ids,
      pricing_rules,
      ...otherShippingDetails
    } = shippingDetails;
    const country_id = selectedCountry;
    const city_ids = Object.values(cities).flat();
    const state_ids = selectedState?.map((data) => data?.value);

    const data = cleanObject({
      ...otherShippingDetails,
      country_id,
      city_ids,
      state_ids,
      storefront_ids,
      pricing_rules: (pricing_rules || [])
        .filter((r) => r.rate !== "")
        .map((r) => cleanObject({ ...r })),
    });

    editShippingMutation.mutate(
      { id: shippingInfo?.id, data },
      {
        onSuccess: () => {
          refetch();
          triggerToast("Shipping updated successfully", "success");
          closeWidget();
        },
        onSettled: () => setRequestLoaderProgress(100),
      },
    );
  };

  const handleAddCityToLocation = (data) => {
    addCityToLocationMutation(data, {
      onSuccess: (data) => {
        setAddCity(data);
        queryClient.invalidateQueries(["getCountryDetails", selectedCountry]);
        triggerToast("city added successfully", "success");
      },
    });
  };

  return {
    menus: menus || [],
    isLoadingMenus,
    countryDetails: countryDetails,
    isLoadingCountryDetails,
    countries: countries,
    cityLocationData,
    addCity,
    isLoadingcityLocationData,
    isLoadingCountries,
    setSelectedState,
    selectInputRef,
    shippingDetails,
    selectedCountry,
    selectedState,
    cities,
    isAddingShipping: createShippingMutation.isLoading,
    isEditingShipping: editShippingMutation.isLoading,
    handleAddCityToLocation,
    setSelectedCountry,
    setCities,
    setShippingDetails,
    handleAddShipping,
    handleEditShipping,
    setCurrency,
    currency,
    addCityModal,
    setAddCityModal,
    getPricingRule,
    upsertPricingRule,
    updatePricingRule,
    removePricingRule,
  };
};

export default useShippingForm;
